import { Card, CardContent, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@ethings/ui-toolkit';
import { Link } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import type { FC, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { FormatTimestamp } from '../../components';
import { useResponsiveText, useSmallScreen, useTermsAndConditionsLabel } from '../../hooks';
import { innerText } from '../../util';
import { TermsAndConditionsApiDocument, TermsAndConditionsDocument } from './profile-page-types';

interface ProfilePageTermsAndConditionsProps {
  documents: TermsAndConditionsDocument[];
}

export const ProfilePageTermsAndConditions: FC<ProfilePageTermsAndConditionsProps> = ({ documents }) => {
  const { t } = useTranslation(['common']);
  const smallScreen = useSmallScreen();
  const { termsLabel } = useTermsAndConditionsLabel();

  const titleText = termsLabel;
  const h5Styles = useResponsiveText('1.5rem', innerText(titleText).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  const renderAction = (document: TermsAndConditionsDocument): JSX.Element | null => {
    if (document.type === 'domain-config') {
      return (
        <Link href={document.url} target="_blank" sx={{ textDecoration: 'none', textTransform: 'uppercase' }}>
          {t('common:page.profile.terms-and-conditions.view.labels')}
        </Link>
      );
    }

    if (document.type === 'api') {
      return <DocumentDownloader document={document} />;
    }

    return null;
  };

  return (
    <Card>
      <CardContent>
        <Typography
          variant="h5"
          style={{
            fontSize: h5Styles.fontSize,
            lineHeight: h5Styles.lineHeight,
            wordBreak: h5Styles.wordBreak,
            whiteSpace: h5Styles.whiteSpace,
            overflow: h5Styles.overflow,
            textOverflow: h5Styles.textOverflow,
          }}
        >
          {titleText}
        </Typography>
      </CardContent>
      {smallScreen
        ? <CardContent style={{ paddingTop: 0 }}>
          <Stack spacing={2}>
            {documents.map((document, i) => (
              <Card key={i} variant="outlined" style={{ padding: 16 }}>
                <Typography variant="subtitle1" style={{ wordBreak: 'break-word' }}>{document.title}</Typography>
                <Typography variant="body2" color="text.secondary" style={{ marginTop: 8 }}>
                  {t('common:page.profile.terms-and-conditions.accepted-on.labels')}: {document.acceptedOn ? <FormatTimestamp value={document.acceptedOn} /> : '-'}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 16 }}>
                  {renderAction(document)}
                </Typography>
              </Card>
            ))}
          </Stack>
        </CardContent>
        : <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common:page.profile.terms-and-conditions.document.labels')}</TableCell>
                <TableCell>{t('common:page.profile.terms-and-conditions.accepted-on.labels')}</TableCell>
                <TableCell>{t('common:page.profile.terms-and-conditions.actions.labels')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document, i) => (
                <TableRow key={i}>
                  <TableCell>{document.title}</TableCell>
                  <TableCell>{document.acceptedOn ? <FormatTimestamp value={document.acceptedOn} /> : null}</TableCell>
                  <TableCell>{renderAction(document)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      }
    </Card>
  );
};

interface DocumentDownloaderProps {
  document: TermsAndConditionsApiDocument;
}

const DocumentDownloader: FC<DocumentDownloaderProps> = ({ document }) => {
  const { t } = useTranslation(['common']);
  const { restClient } = useAuthenticated();
  const { enqueueSnackbar } = useSnackbar();
  const getSignedUrlMutation = useMutation({
    mutationFn: () => restClient.signedDocumentUrl.getTermsAndConditionsSignedDocumentUrl({
      documentId: document.documentId, versionId: document.versionId, language: document.language,
    }),
    onSuccess: ({ signedUrl }) => {
      window.open(signedUrl, '_blank');
    },
    onError: (e) => {
      console.error(e);
      enqueueSnackbar('common:page.profile.terms-and-conditions.failed-to-open.labels', { variant: 'error' });
    },
  });

  return (
    <Link
      component="button"
      disabled={getSignedUrlMutation.isPending}
      sx={(theme) => ({ textDecoration: 'none', textTransform: 'uppercase', color: getSignedUrlMutation.isPending ? theme.palette.text.disabled : undefined })}
      onClick={() => {
        getSignedUrlMutation.mutate();
      }}
    >
      {t('common:page.profile.terms-and-conditions.view.labels')}
    </Link >
  );
};
